import { addLocaleData } from 'react-intl';
import en from 'react-intl/locale-data/en';

import customCategoriesMessages from './containers/CustomCategories/en-US';
import metaProductListingMessages from './containers/MetaProductListing/en-US';
import restrictionListingMessages from './containers/RestrictionListing/en-US';
import syscoSelfServiceMessages from './containers/SyscoSelfService/en-US';
import type { IMessages } from './MessageKeys';

addLocaleData(en);
const languageCode = 'en-US';

const messages: IMessages = {
	breadCrumbs: {
		masterOrderGuides: 'Master Order Guides',
		editMasterOrderGuide: 'Rename Master Order Guide',
		newMasterOrderGuide: 'New Master Order Guide',
		massImport: 'Mass Upload',
		sequenceCharts: 'Sequence Charts',
	},
	containers: {
		admin: {
			newUser: {
				formHeader: 'Add New User',
				formAccountHeader: 'Account',
				formRolesHeader: 'Roles',
				formUserProfileHeader: 'User Profile',
				formLastNamePlaceholder: 'Last Name *',
				formFirstNamePlaceholder: 'First Name *',
				formEmailPlaceholder: 'Email Username *',
				formPasswordPlaceholder: 'Password *',
				formPasswordValidationMessage:
					'Passwords must have at least 10 characters.',
				formSaveButton: 'Save User',
			},
			editUser: {
				firstNameValidationWarning:
					'First name cannot be empty or more than 50 characters.',
				lastNameValidationWarning:
					'Last name cannot be empty or more than 50 characters.',
			},
			users: {
				deleteUserModalError: 'An error occurred while deleting the user',
				deleteUserModalBody:
					'You cannot undo this action. Are you sure you want to delete this user?',
				deleteUserModalTitle: 'Deleting {username}',
				changePasswordToastTitle: 'Success!',
				changePasswordToastBody: 'User password changed.',
				deleteUserToastTitle: 'Success!',
				deleteUserToastBody: 'User deleted.',
				assignmentStatus: 'Assignment Status',
				assigned: 'Assigned',
				unassigned: 'Unassigned',
				customerFilter: 'Customer Filter',
			},
		},
		search: {
			header: 'Product Search',
			searchButton: 'Search',
			addProductsBy: 'Add Products By: ',
			addByProductNumber: 'Product Number',
			noMatchesFound: `We're sorry - no matches were found for "{lastSearch}".`,
			emptySearchMessage:
				'Search for a product in the product search box to see product results.',
			prioritizeButton:
				'Prioritize {count, number} {count, plural, one {Product} other {Products}}',
			prioritizeButtonReadOnly:
				'View {count, number} {count, plural, one {Product} other {Products}}',
			distributionCenterPillLabel:
				'{count, plural, one {Distribution Center} other {Distribution Centers}}',
			multipleDistributionCenterPillValue:
				'{count, number} Distribution Centers Selected',
			multiplePackSizePillValue: '{count, number} Pack Sizes Selected',
		},
		activity: {
			productAdd: 'Product Added to Master Item',
			productRemove: 'Product Removed from Master Item',
			blueprintDelete: 'Master Order Guide Deleted',
			unknown: 'Unknown Action',
		},
		alerts: {
			actions: 'Actions',
			coverageAlerts: 'Coverage Alerts',
			discontinuedAlerts: 'Discontinued Alerts',
			distributionCenterCoverage: 'Distribution Center Coverage',
			locationCoverage: 'Location Coverage',
			masterItem: 'Master Item',
			masterOrderGuide: 'Master Order Guide',
			noAlertsRequiringAttention: 'There are no alerts requiring attention',
			severity: 'Severity',
			distributionCenterHeader: 'Distribution Center',
			output: {
				discontinued:
					'Product #{productNumber} was inactivated at {distributionCenterName}',
				other: 'Other',
			},
			acknowledgeAlertToastMessage: 'Alert Acknowledged.',
			acknowledgeAlertToastTitle: 'Success!',
		},
		browserUnsupported: {
			header: 'Please upgrade your browser to use OGM',

			message:
				'We built OGM using the latest technology which improves the look of the site, increases the speed of the site, and gives you a better experience with new features and functions. Unfortunately, your browser does not support these technologies.',
			resolveHeader:
				'Please download one of these free and up-to-date browsers',
			helpHeader: "I can't update my browser",
			helpListItemZero:
				'Ask your system administrator to update your browser if you cannot install updates yourself.',

			helpListItemOne:
				"If you can't change your browser because of compatibility issues, think about installing a second browser for utilization of this site and keep the old one for compatibility.",
		},
		blueprintManagement: {
			newBlueprint: {
				formHeader: 'New Master Order Guide',
				formMogNamePlaceholder: 'Name',
				formMogNameHeader: 'Master Order Guide',
				formMogSupplierHeader: 'Select a Supplier',
				formMogNameErrorValidation: 'Names must be between 1 and 64 characters',
				successTitle: 'Success!',
				successBody: 'Master Order Guide created',
			},
			deleteBlueprint: {
				successTitle: 'Success!',
				successBody: 'Master Order Guide deleted',
				failureTitle: 'Error',
				failureBody:
					'A problem occurred while attempting to delete the Master Order Guide',
			},
			editBlueprint: {
				formHeader: 'Rename Master Order Guide',
				formMogNamePlaceholder: 'Name',
				formMogNameHeader: 'New Master Order Guide Name',
				formMogNameErrorValidation: 'Names must be between 1 and 64 characters',
				successTitle: 'Success!',
				successBody: 'Master Order Guide changed',
			},
			massImport: {
				downloadTemplate: 'Download Template',
				formHeader: 'Mass Upload',
				formFileInputHeader: 'File to Upload',
				formFileInputErrorValidation: 'File must be of the type .xlsx',
				formSupplierHeader: 'Select a Supplier',
				failureTitle: 'Error Uploading File',
				failureBody:
					'Something went wrong with the file transfer. Please re-upload the file.',
				successTitle: 'Success!',
				successBody:
					'Mass Upload initiated. A report will appear on the Reports page when the mass upload is complete.',
				upload: 'Upload',

				massImportInProgressForCustomer:
					'A mass upload is already in progress for this customer. Its progress can be viewed on the Reports tab.',
				bulkApply: 'Mass Choice Change',
				previewChanges: 'Preview',
				applyChanges: 'Apply Changes',
				acknowledgement: 'I acknowledge this action',
				confirmChanges: 'Confirm Changes',
			},
		},
		login: {
			signInModule: {
				cookiesDisabled: {
					cookiesDisabledHeader: 'Cookies Are Disabled In Your Web Browser',
					cookiesDisabledBody:
						'Our sign on experience requires cookies. Please enable cookies in your browser settings before signing in.',
					cookiesDisabledListHeader:
						'Helpful links to enable cookies for your browser:',
					cookiesDisabledIELabel: 'Internet Explorer or Edge',
					cookiesDisabledChromeLabel: 'Chrome',
					cookiesDisabledFirefoxLabel: 'Firefox',
				},
				signInError: 'Username or Password is incorrect. Failed to sign in.',
				unknownError: "We're sorry. There was a problem with your request.",
				username: 'Username',
				password: 'Password',
			},
		},
		home: {
			newMasterOrderGuide: 'New Master Order Guide',
			allSuppliers: 'All Suppliers',
			allLocations: 'All Locations',
			orderGuideSearchPlaceholder: 'Search Master Order Guides',
			blueprintCard: {
				created: 'Created on {date}',
				locations:
					'{count, number} {count, plural, one {Location} other {Locations}}',
				delete: 'Delete',
				rename: 'Rename',
				items: '{count, number} {count, plural, one {Item} other {Items}}',
			},

			deleteMogWarning:
				'You cannot undo this action. Deleting this master order guide will also delete {metaProductCount} master items from {locationCount} ShipTos.',
		},
		metaProductListing: metaProductListingMessages,
		restrictionListing: restrictionListingMessages,
		restrictionsWorkflow: {
			selectSupplier: 'Select a Supplier',
			header: 'Item Restrictions',
			modalHeader: 'Restricting',
			modalQuestion: 'Would you like to save these restrictions?',
			modalStrongWarning: 'Important Note:',
			modalAdditionalInfo:
				'Any products that are active on your order guides will still be orderable to any ' +
				'locations assigned to those order guides.  To make these products unorderable, please ensure the products ' +
				'are removed from any existing order guides.',
			restrictProducts: 'Restrict Products',
			productNumberHeader: 'Item Numbers to Restrict',
			enterProductNumbers: 'Enter item numbers...',
			enterDefaultRestrictionMessage: 'Default Restriction Message',
			defaultRestrictionMessage: 'This item may not be ordered.',
			numberOfRestrictions:
				'The number of restrictions being added cannot exceed {maxCount}. ' +
				'Please remove {count} restrictions.',
		},
		restrictionLocationAssignment: {
			Title: 'Location Assignment',
		},
		metaProductWorkflow: {
			actions: 'Actions',
			addBySupc: {
				addProducts: 'Add Products',
				addProductsBy: 'Add By Product Number',
				addProductsToNewMasterItem: 'Add Products to New Master Item',
				addProductsToExistingMasterItem: 'Add Products to {item}',
			},
			branding: 'Branding',
			categories: 'Categories',
			coverage: 'Coverage',
			delete: 'Delete',
			demand: 'Demand',
			displaySpecItem: 'Display Spec Item',
			distributionCenterCoverage: 'Distribution Center Coverage',
			addBySearch: 'Search',
			productTable: {
				productNumber: 'Product\u00a0#',
				description: 'Description',
				brand: 'Brand',
				packSize: 'Pack / Size',
				um: 'UM',
				price: 'Price',
				pricePerUnit: 'Price Per Unit',
				proprietary: 'Prop',
				stocking: 'Stocking',
				coverage: 'Coverage',
				priority: 'Priority',
				actions: 'Actions',
				statusActive: 'Active',
				statusInactive: 'Inactive',
				statusDiscontinued: 'Discontinued',
				eligible: 'Eligible',
				ineligible: 'Ineligible',
				manfNumber: 'Manf\u00a0#',
				distributionCenter: 'Distribution Center',
				code: 'Code',
				status: 'Status',
				eligibility: 'Eligibility',
				productAlert: 'Discontinued Item Alert',
				specIndicator: 'Spec Indicator',
			},
			prioritiyNumber: 'Priority #',
			prioritizeMetaProductItems: {
				updateMessageTitle: 'Success!',
				updateMessageBody: 'Master Item updated.',
				addMessageTitle: 'Success!',
				addMessageBody: 'Master Item added.',
				addMasterItem: 'Add Master Item',
				saveMasterItem: 'Save Master Item',
				enterMasterItemName: 'Enter Master Item Name',
				prioritizeTable: {
					collapse: 'Collapse',
					expand: 'Expand',
				},
				recommendationsTitle: 'DSSI Sage Selects',
				categoryFilterTitle: 'Categories and GL Assignments',
				acceptStagedGrouping: 'Accept Grouped Master Item',
				stagedNewGrouping: 'Stage Grouped Master Item',
			},
			rearrange: {
				unsavedChanges:
					'You have unsaved changes. Are you sure you want to leave?',
				distributionCenterOverrides:
					'Any product prioritizations will be cleared upon completing Group/Ungroup Changes. To boost products following any changes here, please go to any Edit Master Item page.',
				metaProductTable: {
					noProducts: 'No Products',
					productNumber: 'Product\u00a0#',
					description: 'Product Title',
					packSize: 'Pack / Size',
					um: 'UM',
					coverage: 'Coverage',
				},
				validationErrors: {
					title: 'Please address the following issues before saving:',
					metaProductName: 'You must enter a Master Item name.',
					metaProductItems:
						'The new Master Item must have one or more products.',
				},
				panes: {
					masterItemDeleteWarning:
						'This Master Item will be deleted upon saving',
					moveSelected: 'Move Selected',
					moveAll: 'Move All',
					addGrouping: 'Add New Master Item',
					undoChanges: 'Undo Changes',
					aiRegrouping: 'Regroup',
					newMasterItem: 'New Master Item Name',
					copiedMasterItem: 'Copied Master Item name to clipboard',
					copyName: 'Copy Name',
				},
			},
			remote: 'Remote',
			nonStock: 'Non\u2011Stock',
			searchForProducts: 'Search For Products',
			searching: 'Search',
			priceLoadingFailedWarning:
				'Something went wrong while getting prices. Please try again.',
			perms: 'Perms',
			permsTitle: 'This product has permutations',
			pendingRebuild:
				'Coverage for this master item is currently being recalculated.',
			specIndicator: 'Spec Indicator',
			selectionTab: 'Select Products',
			coverageTab: 'Distribution Centers & Coverage',
			rulesets: {
				leadTimeSDRN: 'Stocked, Demand, Remote, then Non-Stock',
				USFLeadTime: 'US Foods Stocked, Just In Time, then Call To Order',
				leadTimeSDR: 'Stocked, Demand, then Remote',
				leadTimeSD: 'Stocked, then Demand',
				prioritySDRN: 'Highest Priority including Non-Stock',
				prioritySDR: 'Highest Priority excluding Non-Stock',
				prioritySD: 'Highest Priority excluding Remote, Non-Stock',
				onlyStocked: 'Only Stocked',
				onlyRemote: 'Only Remote',
				unknown: 'Unknown Ruleset {token}',
			},
			aiSettings: {
				auto: 'Auto',
				manual: 'Manual',
				off: 'Off',
				unknown: 'Unknown',
				autoReprioritize: 'Auto Reprioritize',
			},
			isSelectByUm: {
				enabled: 'Enabled',
				disabled: 'Disabled',
				warningModal: {
					header: 'Changing Unit of Measure',
					message:
						'Choose <b>cancel</b> to revert to your previous selection; or choose <b>preview changes</b> to preview these changes and review coverage. Any existing priorities or boosts will be removed.',
					previewChanges: 'Preview Changes',
				},
			},
			distributionCenterCoverageGrid: {
				coverageView: 'Coverage',
				priceView: 'Est. Net Price ($)',
				pricePerUnitView: 'Est. Net Price Per Unit ($)',
			},
			distributionCenterFilterPlaceholder: 'Select Distribution Centers',
			distributionCenterFilterValue: '{dcName}',
		},
		locationAssignment: {
			reviewPrompt: 'Review Changed {locations}',
			removalNotice: 'The following {locations} will be removed:',
			additionNotice: 'The following {locations} will be added:',
			save: 'Save Enabled Locations',
			saveToastMessageTitle: 'Success!',
			saveToastMessageBody: 'Locations updated.',
			locationSelectionTable: {
				name: 'Name',
				id: 'ID',
				addressLine1: 'Address Line 1',
				addressLine2: 'Address Line 2',
				city: 'City',
				state: 'State',
				zip: 'Zip',
				distributionCenterExternalCode: 'DC\u00a0#',
				distributionCenterExternalCodeTitle: 'Distribution Center Number',
				distributionCenterName: 'DC Name',
				distributionCenterNameTitle: 'Supplier Name',
			},
		},
		distributionCenterAssignment: {
			reviewPrompt: 'Review Changed {distributionCenters}',
			removalNotice: 'The following {distributionCenters} will be removed:',
			additionNotice: 'The following {distributionCenters} will be added:',
			save: 'Save Enabled Distribution Centers',
			saveToastMessageTitle: 'Success!',
			saveToastMessageBody: 'Distribution Centers updated.',
			distributionCenterSelectionTable: {
				externalCode: 'DC\u00a0#',
				externalCodeTitle: 'Distribution Center Number',
				name: 'DC Name',
				nameTitle: 'Supplier Name',
			},
		},
		customers: {
			customerList: {
				header: 'Select Your Customer',
				searchPlaceholder: 'Search for Customer',

				noCustomersMessage:
					'No customers are linked to your account. Please contact your OGM user administrator for access.',
			},
		},
		reports: {
			toasts: {
				successTitle: 'Succecss',
				successBody: 'New report sucessfully submitted',
				errorTitle: 'Report Error',
				errorBody: 'A problem occurred while attempting to request new report',
			},
			reportRequestTable: {
				status: {
					pending: 'Pending',
					inProgress: 'In Progress',
					completed: 'Completed',
					error: 'Error',
				},
				reportTypeHeader: 'Name',
				statusHeader: 'Status',
				requestedByHeader: 'Requested By',
				actionsHeader: 'Actions',
				requestAction: 'Generate Report',
				reportRequestCurrentlyProcessing:
					'Report request is currently processing',
				otherReportCurrentlyPending:
					'Another report request is currently pending',
			},
			allCustomers: 'All Customers',
			customerReports: 'Customer Reports',
			requestReports: 'Request Reports',
			generatedReports: 'Generated Reports',
			reportTable: {
				noReportsAvailable: 'There are no reports available.',
				noReportsAvailableForCustomer:
					'There are no reports available for {customer}.',
				customerHeader: 'Customer',
				reportNameHeader: 'Name',
				reportDateHeader: 'Date',
				reportRowCountHeader: 'Row Count',
				actionsHeader: 'Actions',
				downloadLink: 'Download',
				reportNames: {
					chartSyncStatus: 'Chart Sync Status',
					discrepancyDetails: 'Discrepancy Details',
					actionableChartErrors: 'Actionable Chart Errors',
					stockMatrix: 'Stock Matrix',
					massImport: 'Mass Import',
					phaseOutAutoAddEnabled: 'Phase Out Auto-Add Enabled',
					phaseOutAutoAddDisabled: 'Phase Out Auto-Add Disabled',
					massLocationAdd: 'Mass Location Add',
					orderGuide: 'Order Guide Download',
					aramarkDiscrepancyDetails: 'Aramark Discrepancy Details',
					aramarkFileStatus: 'Aramark File Status',
					aramarkProprietaryItem: 'Aramark Proprietary Items',
					aramarkOpcoSupcChangesSummary: 'Aramark Opco SUPC Changes Summary',
					aramarkFullCatalogSummary: 'Aramark Full Catalog Summary',
					discontinuedItems: 'Discontinued Items',
					aggregateProprietaryItems: 'Proprietary Ineligible',
					ogmAiHistory: 'OGM.AI History',
					ogmAiOptimizations: 'OGM.AI Optimizations',
					locationLevelPricing: 'Location Level Pricing',
					bulkApply: 'Mass Choice Change',
					ogmAiGroupings: 'OGM.AI Groupings',
					ogmAiCombined: 'OGM.AI Combined',
					offOrderGuideSavings: 'Off Order Guide Savings',
					massCustomerItemNumberImport: 'Mass Customer Item Number Import',
					CustomerItemNumberMappings: 'Customer Item Number Mappings',
				},
			},
		},
		orderGuideDownload: {
			requestModal: {
				header: 'Apply Filters To Report',
				subHeader: 'Filters for Customer: {customerName}',
				submitButton: 'Apply',
				filterPlaceholder: 'Select {filter}s',
				filterLabels: {
					distributionCenter: '{supplierName} - {dcName} - {dssiCode}',
				},
				dcMessage: 'Export as one tab per DC',
				filterLocationPlaceholder: 'Select Locations',
			},
		},
		stockMatrix: {
			requestModal: {
				header: 'Apply Filters To Report',
				subHeader: 'Filters for Customer: {customerName}',
				submitButton: 'Apply',
				filterPlaceholder: 'Select {filter}s',
				filterLabels: {
					distributionCenter: '{supplierName} - {dcName} - {dssiCode}',
				},
				filterLocationPlaceholder: 'Select Locations',
			},
		},
		MassCustomerItemNumberImport: {
			toasts: {
				errorTitle: 'Error Uploading File',
				errorBody:
					'Something went wrong with the file transfer. Please re-upload the file.',
				successTitle: 'Success!',
				successBody:
					'Mass Upload initiated. A report will appear on the Reports page when the mass upload is complete.',
			},
		},
		syscoSelfService: syscoSelfServiceMessages,
		customCategories: customCategoriesMessages,
		singleSignOn: {
			sessionExpired: 'Your session has timed out due to inactivity.',
		},
	},
	components: {
		addBySupcModal: {
			enterProductNumbers: 'Enter Product Numbers...',
		},
		dataTable: {
			searchTable: 'Search Table',
		},
		navigationBar: {
			masterOrderGuides: 'Master Order Guides',
			orderGuideDownload: 'Order Guide Download',
			restrictions: 'Restrictions',
			manageUsers: 'Manage Users',
			alerts: 'Alerts',
			signOut: 'Sign Out',
			changeCustomer: 'Change Customer',
			reports: 'Reports',
		},
		proprietary: {
			allProprietaryLabel: 'All',
			allProprietaryTitle:
				'Product is Proprietary at all Distribution Centers.',
			someProprietaryLabel: 'Some',
			someProprietaryTitle:
				'Product is Proprietary at one or more Distribution Centers.',
		},
		branding: {
			isBranded: 'Item is supplier branded.',
			isBrandedDetail: 'Item is supplier branded.',
		},
		userProfileCard: {
			accountHeader: 'Account',
			rolesHeader: 'Roles',
			userProfileHeader: 'User Profile',
			lastNamePlaceHolder: 'Last Name',
			firstNamePlaceHolder: 'First Name',
			saveButton: 'Save Settings',
		},
		productDetailsSlideout: {
			description: 'Description',
			productNumber: 'Product #',
			packSize: 'Pack\u00a0/\u00a0Size',
			brand: 'Brand\u00a0/\u00a0Manufacturer',
			details: 'Details',
			foodNutrition: 'Food & Nutrition',
			gtin: 'GTIN',
			catchweight: 'Catch Weight',
			locationLevelPricing: 'Location-Level Pricing',
		},
		nutritionLabel: {
			servingSize: 'Serving Size',
			nutritionFacts: 'Nutrition Facts',
			amountPerServing: 'Amount Per Serving',
			calories: 'Calories',
			caloriesFromFat: 'Calories from Fat',
			percentDailyValue: '% Daily Value',
			percentDailyValueTop: '% Daily Value *',
			totalFat: 'Total Fat',
			saturatedFat: 'Saturated Fat',
			transFat: 'Trans Fat',
			monounsaturatedFat: 'Monounsaturated Fat',
			cholesterol: 'Cholesterol',
			sodium: 'Sodium',
			totalCarbs: 'Total Carbohydrates',
			dietaryFiber: 'Dietary Fiber',
			sugars: 'Sugars',
			protein: 'Protein',
			vitaminD: 'Vitamin D',
			calcium: 'Calcium',
			iron: 'Iron',
			potassium: 'Potassium',
			dailyValueDescription:
				'* The % Daily Value (DV) tells you how much a nutrient in a serving of food' +
				'contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.',
			ingredients: 'INGREDIENTS',
		},
		coverageMessages: {
			oneAdditional:
				'Product provides coverage for 1 additional distribution center',
			noAdditionalCoverage:
				'Product provides no additional distribution center coverage',
			multipleAdditional: `Product provides coverage for {additionalCoverageCount} additional distribution centers`,
		},
		rebatePill: {
			rebate: 'Rebate',
			contract: 'Contract',
			rebateAvailable: 'Rebate Available',
		},
		priceAttributes: {
			rebate: 'Rebate',
			contract: 'Contract',
			audited: 'Audited Price',
			rebateAvailable: 'Rebate Available',
		},
		specIndicator: {
			hard: 'Hard',
			required: 'Required',
			open: 'Open',
			unassigned: 'Unassigned',
			edit: 'Edit',
			spec: 'Spec',
		},
		suggestionDetail: {
			dismissStaleOptimization: 'Dismiss Stale Optimization',
		},
	},
	validation: {
		errorOnPageLoad:
			'Something went wrong with your request. Please reload the page and try again.',
		nameCannotBeEmpty: 'Name cannot be empty',
		generic: 'Something went wrong',
	},
	common: {
		save: 'Save',
		apply: 'Apply',
		cancel: 'Cancel',
		edit: 'Edit',
		delete: 'Delete',
		roles: {
			userAdmin: 'User Admin',
			readOnly: 'Read Only',
			externalUser: 'External User',
			categoryAdmin: 'Custom Category Admin',
			ogmAI: 'OGM.AI',
			ogmAITrial: 'OGM.AI Trial',
		},
		central: 'Central',
		distributionCenter: 'Distribution Center',
		distributionCenters: 'Distribution Centers',
		location: 'Location',
		locations: 'Locations',
	},
	copyrightMessages: {
		copyright:
			'© 2017-' + new Date().getFullYear() + ' DSSI, All rights reserved.',
		confidentialityNotice: 'Strictly Confidential.',
	},
	privacyMessages: {
		loginNoticeTerms: 'By signing in, you agree to our ',
		loginNoticePrivacy: 'and acknowledge you have read our ',
		termsOfUse: 'Terms of Use',
		privacyPolicy: 'Privacy Policy',
		cookiePolicy: 'Cookie Policy',
		californiaCollectionNotice: 'California Collection Notice',
		privacyChoices: 'Your Privacy Choices',
	},
};

export { languageCode, messages };

export default messages;
